import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

import { useReisefotos } from '../../hooks/queries'
import type { Media, Reisefotos, Reisefotoziel } from '../../api/types'
import { fehlertext } from '../../api/client'
import { zeitText, type Uhr } from '../../lib/ortszeit'
import { Standbild } from '../../components/Standbild'
import { dauerText, standbildAdresse } from '../../lib/video'
import { useTexte, WORTLAUT } from '../../texte'
import { useEscape } from '../../hooks/useEscape'
import { Blattkopf } from '../../components/Blattkopf'

/** Ein Bild aus der Reise -- mit der Auskunft, woran es schon hängt. */
type Reisefoto = Reisefotos['media'][number]

/**
 * Where the chosen pictures are going (1.52.0) -- the picker sorts and
 * offers by it.
 *
 * - `punkt`: the point's form. What hangs at another point is taken, the
 *   rest is offered, the ones near the point in place and time first.
 * - `spot`: the spot's sheet. The point's own pictures come first, as a
 *   group of their own: chosen there, they **move** into the spot. Voice
 *   notes stay at the point -- the server refuses them, and a tile that
 *   cannot pass is the worst answer.
 * - `chat`: the line of the chat. Offered is what hangs at a point or spot
 *   and is not in the chat yet -- from use: "auch im Chat: Fotos aus dieser
 *   Reise". No place to sort against, so no "passt" group.
 */
export type Fotowahlziel =
  | { art: 'punkt'; lat: number; lon: number; wann?: string }
  | { art: 'spot'; lat: number; lon: number; highlightId: number; poiId: number }
  | { art: 'chat' }

/**
 * Fotos anhängen, die schon auf dem Server liegen.
 *
 * **Der zweite Weg zum Bild.** Der erste geht in die Foto-App des Geräts und
 * lädt hoch; dieser nimmt, was ohnehin schon da ist — meist ein Bild, das im
 * Chat steht und jetzt auch an den Punkt auf der Karte gehört. Es wird dabei
 * **nicht kopiert und nicht weggenommen**: dieselbe Datei hängt danach an
 * beiden Stellen, und der Chatbeitrag verliert nichts. **Eine Ausnahme, mit
 * Ansage** (1.52.0): vom Punkt in seinen Spot wird verschoben, nicht
 * verdoppelt -- der Spot ist ein Teil des Punktes, und dasselbe Foto in
 * beiden Listen wäre zweimal dasselbe.
 *
 * Angeboten werden Bilder **und Videos**, aber nur die **eigenen** dieser Reise. Fremde sind
 * Antworten von Mitlesenden — eines davon in den Reisebericht zu heben, wäre
 * genau das Leck, das die eine Regel verhindert. Der Server liefert deshalb
 * auch nur die eigenen aus; die Auswahl hier ist die Anzeige davon, nicht die
 * Prüfung.
 *
 * **Was zu diesem Punkt passt, steht oben.** Aus dem Betrieb: bei zweihundert
 * Bildern einer Reise ist eine Liste nach Datum keine Auswahl, sondern eine
 * Suche. Gemessen wird wie bei den Vorschlägen aus der Route — derselbe
 * Suchradius, dieselbe halbe Stunde Luft. **Und gemessen wird auf dem
 * Server** (1.53.3): die Auswahl nennt ihm das Ziel, er sagt zu jedem Bild,
 * ob es passt, liefert alles Passende und alles vom Punkt vollständig und
 * deckelt nur den Rest (`picker_max_media`, Vorgabe 50). Vorher schnitt der
 * Server die 300 neuesten ab, und erst der Browser sortierte -- mit 50
 * hätten die passenden Bilder eines älteren Punktes gefehlt. Eine Regel,
 * eine Stelle.
 *
 * **Jede Kachel sagt, was sie ist.** „im Chat" heißt: das Bild steht schon in
 * einem Beitrag. „am POI" und „am Spot" sagen, wo es sonst hängt. **„noch
 * nirgends"** heißt: es liegt auf dem Server und gehört
 * zu nichts — ein abgestürzter Browser, ein Reiter, der mitten im Hochladen
 * zuging. Solche Bilder räumt der Server nach einem Tag weg; wer eines behalten
 * will, hängt es hier an. Und „ohne Datum" sagt mehr als eine leere Ecke:
 * ein Bild ohne Aufnahmedaten kann nicht zu einem Punkt passen, egal wie nah
 * es entstanden ist.
 */
export function Fotowahl({
  tripId,
  schonDa,
  ziel,
  versatz,
  onFertig,
  onSchliessen,
}: {
  tripId: number
  /** Was am Entwurf schon hängt — doppelt anhängen ergibt nichts. */
  schonDa: number[]
  /** Wohin die Auswahl geht -- und damit, was angeboten wird. */
  ziel: Fotowahlziel
  /** In welcher Uhr die Zeiten auf den Kacheln stehen sollen. */
  versatz: Uhr
  onFertig: (bilder: Media[]) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.karte.fotowahl
  const fotos = useReisefotos(tripId, true, zielFuerServer(ziel))
  const [gewaehlt, setGewaehlt] = useState<number[]>([])

  const alle = (fotos.data?.media ?? []).filter((m) => !schonDa.includes(m.id))
  const { vomPoi, passend, weitere, vergeben, toene } = einteilen(ziel, alle)
  const angeboten = [...vomPoi, ...passend, ...weitere]
  const fehlen = fotos.data?.more ?? 0

  const kachel = (foto: Reisefoto) => {
    const an = gewaehlt.includes(foto.id)
    return (
      <button
        type="button"
        key={foto.id}
        className={an ? 'fotowahl__bild fotowahl__bild--an' : 'fotowahl__bild'}
        aria-pressed={an}
        onClick={() =>
          setGewaehlt((alt) =>
            alt.includes(foto.id) ? alt.filter((id) => id !== foto.id) : [...alt, foto.id],
          )
        }
      >
        {/* **Eine Tonaufnahme hat kein Bild, und das ist kein Fehler** --
            dieselbe Kachel wie im Formular des Punktes (`bogen__ton`): ein
            Notenzeichen statt eines leeren `Standbild`s. Angeboten wird sie
            nur, wo sie hin darf (im Chat), s. `einteilen()`. */}
        {foto.kind === 'audio' ? (
          <span className="bogen__ton" aria-hidden="true">♪</span>
        ) : (
          /* **Ein Video zeigt sein Standbild.** Die Datei selbst zu laden hieße,
             Megabyte zu holen, damit ein Daumennagel etwas zeigt.

             **`Standbild` und kein schlichtes `<img>`** (Codereview D2). Ein
             Standbild wird nach dem Video hochgeladen; `standbildAdresse()`
             leitet die Adresse ab, die es haben wird, und die gibt eine Weile
             404. Ein gewöhnliches Bild versucht es genau einmal und bleibt
             danach leer -- hier stand also eine kaputte Kachel, während
             Formular, Stapel und Fotoseite längst nachfragen. */
          <Standbild
            /* Auch hier die Kachel und nicht das Original (#57). */
            src={standbildAdresse(foto) ?? ''}
            loading="lazy"
          />
        )}
        {foto.kind === 'video' && (
          <span className="fotowahl__dauer">
            ▶ {foto.duration_ms > 0 ? dauerText(foto.duration_ms) : t.video}
          </span>
        )}
        {foto.kind === 'audio' && (
          <span className="fotowahl__dauer">
            ♪ {foto.duration_ms > 0 ? dauerText(foto.duration_ms) : t.tonaufnahme}
          </span>
        )}
        {/* Woher das Bild schon bekannt ist -- sonst rätselt man, warum
            dasselbe Foto zweimal auftaucht. Und wenn es nirgends herkommt,
            steht auch das da: eine Kachel ohne Marke war bisher ein Rätsel. */}
        {foto.in_chat ? (
          <span className="fotowahl__marke">{t.imChat}</span>
        ) : foto.at_highlight ? (
          <span className="fotowahl__marke">{foto.poi_id !== null ? t.amSpot : t.amPoi}</span>
        ) : (
          <span className="fotowahl__marke fotowahl__marke--waise">{t.nirgends}</span>
        )}
        <span className={foto.taken_at ? 'fotowahl__wann' : 'fotowahl__wann fotowahl__wann--ohne'}>
          {foto.taken_at ? zeitText(foto.taken_at, versatz) : t.ohneDatum}
        </span>
      </button>
    )
  }

  // Escape schliesst -- wie die Grossansicht der Fotos. Ein Blatt, das nur
  // auf das X hoert, ist am Rechner eine Sackgasse.
  useEscape(onSchliessen)

  // **Beim Aufgehen das Feld loslassen.** Sonst bliebe die Tastatur stehen
  // und deckte die Auswahl zur Hälfte zu; gewünscht aus dem Betrieb.
  useEffect(() => {
    const feld = document.activeElement
    if (feld instanceof HTMLElement) feld.blur()
  }, [])

  const wahl = angeboten.filter((m) => gewaehlt.includes(m.id))

/**
 * **Ein Blatt über dem Blatt gehört an den Seitenkörper.**
 *
 * Aus dem Betrieb, mit Bildschirmfoto (#33): „Aufruf der Spotlight
 * hinzufügen Seite: manchmal wird die Karte richtig angezeigt, manchmal nur
 * verkrüppelt." Gemeint war ein Übereinander -- diese Auswahl wurde
 * **innerhalb** des Formular-Blattes gezeichnet, mit derselben Klasse und
 * demselben Rang. Zwei Blätter in einem Stapel statt eines obenauf: das
 * untere schien durch, das obere ging hinter Tastatur und iOS-Leiste auf.
 *
 * Derselbe Griff wie bei der Großansicht der Fotos (`thread/Gallery.tsx`)
 * und aus demselben Grund: was obenauf liegt, gehört keinem Kasten, sondern
 * dem Bildschirm. Dazu ein eigener Rang (`.bogen--obenauf`), damit die
 * Reihenfolge nicht am Zufall der DOM-Reihenfolge hängt.
 *
 * **Und die Tastatur geht zu.** Aus dem Betrieb gewünscht: „Spot hinzufügen
 * soll die Tastatur schließen." Wer hier ankommt, tippt nicht mehr, sondern
 * wählt -- und eine offene Tastatur nähme der Auswahl die halbe Fläche.
 */
  return createPortal(
    <div
      className="bogen bogen--obenauf"
      role="dialog"
      aria-modal="true"
      aria-label={t.titel}
      data-fotowahl-ziel={ziel.art}
    >
      {/* **Head, list, foot as siblings** (1.53.3, from use: "so scrollbar,
          dass die Buttons unten leicht erreichbar sind"). The sheet was the
          scroll box and the buttons its last child -- a hundred rows down
          with three hundred tiles. Now only `.bogen__rolle` scrolls; the
          rule for why not `position: sticky` stands at the CSS. */}
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <Blattkopf titel={t.titel} onSchliessen={onSchliessen} />

        <div className="bogen__rolle">
        {/* **Ein Fehler steht da, keine leere Liste** (1.52.0). Ohne Netz
            kam die Liste nicht, und hier stand „nichts da" -- eine stille
            Absage mit falschem Wortlaut. */}
        {fotos.isPending ? (
          <p className="bogen__hinweis">{t.laedt}</p>
        ) : fotos.isError ? (
          <p className="bogen__hinweis" role="alert">{fehlertext(fotos.error)}</p>
        ) : angeboten.length === 0 ? (
          <p className="bogen__hinweis">
            {ziel.art === 'chat' ? t.nichtsFuerChat : vergeben.length > 0 ? t.allesVergeben : t.nichtsDa}
          </p>
        ) : (
          <>
            {vomPoi.length > 0 && (
              <>
                <p className="fotowahl__gruppe">{t.ausDiesemPoi}</p>
                <div className="fotowahl" data-fotowahl-gruppe="poi">{vomPoi.map(kachel)}</div>
              </>
            )}
            {passend.length > 0 && (
              <>
                <p className="fotowahl__gruppe">{t.passt}</p>
                <div className="fotowahl" data-fotowahl-gruppe="passt">{passend.map(kachel)}</div>
              </>
            )}
            {weitere.length > 0 && (
              <>
                {(passend.length > 0 || vomPoi.length > 0) && (
                  <p className="fotowahl__gruppe">{t.weitere}</p>
                )}
                <div className="fotowahl" data-fotowahl-gruppe="weitere">{weitere.map(kachel)}</div>
              </>
            )}
          </>
        )}

        {/* Die vergebenen zum Schluss, sichtbar und grau: sie zu verschweigen
            hieße, dass man sie sucht und nicht findet. */}
        {vergeben.length > 0 && (
          <p className="bogen__hinweis">
            {t.vergeben(vergeben.length)} {t.vergebenZusatz}
          </p>
        )}
        {toene > 0 && <p className="bogen__hinweis">{t.keineToene}</p>}
        {/* What stayed behind the cap (1.53.3) -- named, not hidden: a
            picture that is not offered should at least be missed for a
            reason. Never for the chat, which has no target and the old cap. */}
        {fehlen > 0 && ziel.art !== 'chat' && (
          <p className="bogen__hinweis" data-fotowahl-fehlen={fehlen}>{t.aeltereFehlen(fehlen)}</p>
        )}
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button button--outline button--small"
            onClick={onSchliessen}
          >
            {texte.allgemein.abbrechen}
          </button>
          <button
            type="button"
            className="button"
            disabled={wahl.length === 0}
            onClick={() => onFertig(wahl)}
          >
            {beschriftung(wahl, ziel, vomPoi)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Who is offered where -- one function, three targets (1.52.0).
 *
 * `vergeben` is what hangs somewhere the server would refuse: at another
 * point, or in a sibling spot of the same point. `toene` counts the voice
 * notes a spot cannot take; they are named in a sentence, not hidden.
 */
function einteilen(
  ziel: Fotowahlziel,
  alle: Reisefoto[],
): { vomPoi: Reisefoto[]; passend: Reisefoto[]; weitere: Reisefoto[]; vergeben: Reisefoto[]; toene: number } {
  if (ziel.art === 'chat') {
    return {
      vomPoi: [],
      passend: [],
      weitere: alle.filter((m) => m.at_highlight && !m.in_chat),
      vergeben: [],
      toene: 0,
    }
  }

  const bilder = ziel.art === 'spot' ? alle.filter((m) => m.kind !== 'audio') : alle
  const toene = alle.length - bilder.length
  const vomPoi =
    ziel.art === 'spot'
      ? bilder.filter((m) => m.highlight_id === ziel.highlightId && m.poi_id === null)
      : []
  // **Was schon an einem Punkt hängt, ist vergeben.** Der Server hängt ein
  // Bild nicht um (er nimmt nur Bilder ohne `highlight_id`) -- anwählbar zu
  // sein und dann nicht zu passieren wäre die schlechteste Antwort. Aus dem
  // Betrieb gemeldet: „2 Fotos anhängen", und eines kam nicht an.
  const frei = bilder.filter((m) => !m.at_highlight)
  const vergeben = bilder.filter((m) => m.at_highlight && !vomPoi.includes(m))

  // Whether a picture matches is the server's word (1.53.3, `passt`); the
  // picker only sorts by it.
  const passend = frei.filter((m) => m.passt)
  const weitere = frei.filter((m) => !m.passt)
  return { vomPoi, passend, weitere, vergeben, toene }
}

/**
 * What the server is told about the target (1.53.3): place and time of a
 * point, place and the point's id for a spot -- and nothing for the chat,
 * which has no place to measure against and gets the old cap.
 */
function zielFuerServer(ziel: Fotowahlziel): Reisefotoziel | undefined {
  if (ziel.art === 'punkt') return { lat: ziel.lat, lon: ziel.lon, ...(ziel.wann === undefined ? {} : { at: ziel.wann }) }
  if (ziel.art === 'spot') return { lat: ziel.lat, lon: ziel.lon, highlight: ziel.highlightId }
  return undefined
}

/**
 * Was auf dem Knopf steht.
 *
 * **Die Auswahl sagt, was sie anhängt.** „2 anhängen" liesse offen, was da
 * gleich am Punkt landet; solange nur eine Art gewählt ist, wird sie genannt.
 * Gemischt geht das nicht mehr — dann bleibt die Zahl allein stehen. **Und
 * ein Umzug heißt Umzug** (1.52.0): kommt alles Gewählte vom Punkt, sagt der
 * Knopf, dass es dort verschwindet.
 */
function beschriftung(gewaehlt: Reisefoto[], ziel: Fotowahlziel, vomPoi: Reisefoto[]): string {
  const t = WORTLAUT.karte.fotowahl
  if (gewaehlt.length === 0) return t.auswaehlen
  if (ziel.art === 'spot' && gewaehlt.every((m) => vomPoi.includes(m))) return t.umziehen(gewaehlt.length)

  const videos = gewaehlt.filter((m) => m.kind === 'video').length
  const toene = gewaehlt.filter((m) => m.kind === 'audio').length
  if (videos === 0 && toene === 0) return t.fotosAnhaengen(gewaehlt.length)
  if (videos === gewaehlt.length) return t.videosAnhaengen(videos)
  if (toene === gewaehlt.length) return t.toeneAnhaengen(toene)
  return t.gemischtAnhaengen(gewaehlt.length)
}
